import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useCorpse } from './useCorpse';
import { getZonePreset, ZONE_COLOUR_CLASSES } from './corpseAssignment';
import GameLobby from '../shared/GameLobby';
import { Clock, Scissors, Layers } from 'lucide-react';

const DURATION_OPTIONS = [
  { label: '30s', value: 30 },
  { label: '60s', value: 60 },
  { label: '90s', value: 90 },
];

const ZONE_COUNT_OPTIONS = [
  { label: '3 Zones', value: 3, desc: 'Head · Body · Legs — the classic' },
  { label: '4 Zones', value: 4, desc: 'Head · Torso · Hips · Legs' },
  { label: '5 Zones', value: 5, desc: 'Head · Shoulders · Torso · Hips · Legs' },
];

export default function CorpseLobby() {
  const { state } = useApp();
  const { game, user } = state;
  const { initCorpse, startCorpse, endGame } = useCorpse();

  const [zoneDuration, setZoneDuration] = useState(60);
  const [zoneCount, setZoneCount] = useState(3);
  const [initialised, setInitialised] = useState(false);

  const memberCount = Object.keys(state.room.members || {}).length;
  const totalCreatures = Math.max(1, Math.ceil(memberCount / zoneCount));
  const activeZoneCount = game.zoneCount || zoneCount;
  const activePreset = getZonePreset(activeZoneCount);

  const handleInit = () => {
    initCorpse({ zoneDuration, zoneCount });
    setInitialised(true);
  };

  return (
    <GameLobby
      title="Exquisite Corpse"
      emoji="🧟"
      onStart={initialised ? startCorpse : handleInit}
      onCancel={endGame}
      startLabel={initialised ? 'Start Drawing!' : 'Set Up'}
      startDisabled={memberCount < 1}
    >
      {user.isHost ? (
        <div className="space-y-4">
          {/* Zone count */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              Zones Per Creature
            </p>
            <div className="space-y-1.5">
              {ZONE_COUNT_OPTIONS.map(({ label, value, desc }) => (
                <button
                  key={value}
                  onClick={() => setZoneCount(value)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-display transition-all text-left ${
                    zoneCount === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <span>{label}</span>
                  <span className={`text-xs ${zoneCount === value ? 'text-white/70' : 'text-slate-400'}`}>
                    {desc}
                  </span>
                </button>
              ))}
            </div>
            {memberCount > 3 && zoneCount === 3 && (
              <p className="text-xs text-amber font-display mt-1.5">
                Tip: with {memberCount} players, try {memberCount >= 5 ? '5' : '4'} zones so everyone draws each creature
              </p>
            )}
          </div>

          {/* Time per zone */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Time Per Zone
            </p>
            <div className="flex gap-2">
              {DURATION_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setZoneDuration(value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    zoneDuration === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Creature count preview */}
          <div className="bg-amber/5 border border-amber/20 rounded-xl p-3 flex items-center gap-3">
            <Scissors className="w-5 h-5 text-amber shrink-0" />
            <p className="text-sm text-charcoal font-display">
              <span className="font-semibold">{memberCount} player{memberCount !== 1 ? 's' : ''}</span>
              {' × '}
              <span className="font-semibold">{zoneCount} zones</span>
              {' = '}
              <span className="font-semibold">{totalCreatures} creature{totalCreatures !== 1 ? 's' : ''}</span>
            </p>
          </div>

          {/* Zone preview */}
          <div className="bg-slate-50 rounded-xl p-3 space-y-2">
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide">Zone Layout</p>
            <div className="flex gap-1.5">
              {getZonePreset(zoneCount).map((zone) => {
                const colours = ZONE_COLOUR_CLASSES[zone.colour];
                return (
                  <div
                    key={zone.name}
                    className={`flex-1 text-center py-2 rounded-lg text-xs font-display font-medium ${colours.bg} ${colours.text}`}
                  >
                    {zone.emoji} {zone.name}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 font-display leading-relaxed">
              Each player draws a zone without seeing the rest. Zones overlap slightly so
              the creature connects. The full creation is revealed at the end!
            </p>
          </div>

          {/* Historical blurb */}
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-300 font-display leading-relaxed italic">
              Cadavre Exquis — invented by the Surrealists circa 1925 at 54 rue du Château, Paris.
              André Breton, Jacques Prévert, Yves Tanguy, and Marcel Duhamel folded paper and drew
              without seeing each other&apos;s work. The name came from their first collaborative sentence:
              &ldquo;Le cadavre exquis boira le vin nouveau&rdquo; — The exquisite corpse shall drink the new wine.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {game.zoneDuration && (
            <div className="bg-amber/5 border border-amber/20 rounded-xl p-3">
              <p className="text-xs text-amber font-display uppercase tracking-wide mb-1">Time Per Zone</p>
              <p className="text-sm font-semibold text-charcoal font-display">{game.zoneDuration}s</p>
            </div>
          )}
          {game.zoneCount && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-2">
              <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-1">Zones</p>
              <div className="flex gap-1.5">
                {activePreset.map((zone) => {
                  const colours = ZONE_COLOUR_CLASSES[zone.colour];
                  return (
                    <div
                      key={zone.name}
                      className={`flex-1 text-center py-1.5 rounded-lg text-xs font-display font-medium ${colours.bg} ${colours.text}`}
                    >
                      {zone.emoji} {zone.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-1">Creatures</p>
            <p className="text-sm text-charcoal font-display">
              {totalCreatures} creature{totalCreatures !== 1 ? 's' : ''} to create
            </p>
          </div>
          <p className="text-sm text-slate-400 font-display text-center">
            Waiting for host to start...
          </p>
        </div>
      )}
    </GameLobby>
  );
}
